import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as R from "ramda";
import cn from "classnames";
import * as Sentry from "@sentry/browser";
import { Alert, Modal, Button, Nav, AlertProps } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useModalInfo } from "../redux/modals";
import { isInternalSelector, useUpToDate } from "../redux/user";
import { useCompanyInfo } from "../redux/company";
import ReactGA from "react-ga4";
import { Img } from "./Img";
import "./Page.scss";
import { Mixpanel } from "../utils/mixpanelWrapper";
import { BugTicketModal } from "../Components/BugTicketModal";
import VideoTutorial from "../VideoTutorial/VideoTutorial";
import { VideoInfo } from "../VideoTutorial/videoTutorialUtils";
import { getVideosForPage } from "../VideoTutorial/videoConfig";

export const AlertBar: React.FC = ({ children }) => {
  const { dismiss } = useUpToDate();

  return (
    <div className="newVersionAlertBar">
      <div>{children}</div>
      <Button variant="link" size="sm" onClick={dismiss}>
        Dismiss
      </Button>
    </div>
  );
};

const isJSONParseable = (input: string): boolean => {
  try {
    JSON.parse(input);
    return true;
  } catch (error) {
    return false;
  }
};

export const NewVersionAlertBar: React.FC = () => {
  const { appUpToDate } = useUpToDate();

  if (appUpToDate) {
    return null;
  }
  return <AlertBar>There is a new version of the app. Please refresh the page.</AlertBar>;
};

export const formatMessage = (message?: React.ReactNode): typeof message => {
  if (message && typeof message === "string") {
    return message.split("\n").map((line, i) => {
      let parts = line.split(/(`.+?`)/);
      return (
        <p key={i}>
          {parts.map((part, i) =>
            part.startsWith("`") ? <code key={`part${i}`}>{part.slice(1, -1)}</code> : part
          )}
        </p>
      );
    });
  }
  return message;
};

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    console.error(error);
    Sentry.captureException(error);
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="errorBoundary">
          An unknown error has occurred. Details have been sent to the engineering team. Please try
          again later.
        </div>
      );
    }
    return this.props.children;
  }
}

export interface PageNav {
  label: string;
  key: string;
}

export interface PageProps {
  title: React.ReactNode;
  pageType: string;
  actions?: React.ReactNode;
  navs?: PageNav[];
  navRenderer?: (label: string) => JSX.Element;
  onNav?: (nav: string) => void;
  selectedNav?: string;
  subHeader?: React.ReactNode;
  minWidth?: string;
  minHeight?: string;
  onlyModals?: boolean;
  app2Redesign?: boolean;
}

export const Page: React.FC<PageProps> = ({
  children,
  title,
  pageType,
  actions,
  navs,
  navRenderer = label => label,
  onNav,
  selectedNav,
  subHeader,
  minWidth = "1220px",
  minHeight = "400px",
  onlyModals = false,
  app2Redesign = false,
}) => {
  const [showBugTicketModal, setShowBugTicketModal] = useState<boolean>(false);
  const [showHelpVideoTutorial, setShowHelpVideoTutorial] = useState<boolean>(false);
  const [videoList, setVideoList] = useState<VideoInfo[]>([]);
  const { error, areYouSure } = useModalInfo();
  const [initialNav] = useState(selectedNav || (navs && navs[0] ? navs[0].key : undefined));
  const [mixpanelTracked, setMixpanelTracked] = useState(false);

  const ourOnNav = useCallback(
    (eventKey: string | null) => {
      if (onNav && !R.isNil(eventKey)) {
        Mixpanel.register({ active_page: eventKey });
        Mixpanel.track_pageview({ page: eventKey });
        onNav(eventKey);
      }
    },
    [onNav]
  );
  const { cid } = useCompanyInfo();
  const isInternal = useSelector(isInternalSelector);

  // Used for GA4 tracking of page types.  Simpler than the <Page /> title, since that can
  // sometimes be a JSX element instead of just a string.
  useEffect(() => {
    if (pageType) {
      document.title = pageType || "None";

      ReactGA.initialize("G-LCZYGZVKT2", {
        gtagOptions: {
          role: isInternal ? "internal" : "external",
          company: cid || "None",
        },
      });
      ReactGA.send("pageview");
    }
  }, [pageType, isInternal, cid]);

  // temporary remove after 1 day
  useEffect(() => {
    Mixpanel.unregister("page");
  }, []);

  const errorParams = useMemo(() => {
    if (!error || typeof error.message !== "string") {
      return { message: "", path: "", params: {} };
    }
    const raw = error.message;
    // Regex to split off the JSON blob
    const match = raw.match(/^(.*?)(\{[\s\S]*\})\s*$/);
    const userText = match ? match[1].trim() : raw;
    const jsonBlob = match ? match[2] : null;

    if (jsonBlob && isJSONParseable(jsonBlob)) {
      const parsed = JSON.parse(jsonBlob);

      // Check if there's nested JSON in the message field
      if (parsed.message && typeof parsed.message === "string" && isJSONParseable(parsed.message)) {
        const inner = JSON.parse(parsed.message);
        const merged = { ...inner, ...parsed };

        return {
          message: `${userText} Error: ${merged.message || "Unknown error"}`,
          path: merged.path || "",
          params: merged.params || merged.queryStringParameters || {},
        };
      }

      // No nested JSON, use parsed object directly
      return {
        message: `${userText} Error: ${parsed.message || "Unknown error"}`,
        path: parsed.path || "",
        params: parsed.params || parsed.queryStringParameters || {},
      };
    }
    // No json found
    return {
      message: userText,
      path: "",
      params: {},
    };
  }, [error]);

  useEffect(() => {
    const list = getVideosForPage(pageType);
    if (list.length && isInternal) {
      setShowHelpVideoTutorial(true);
      setVideoList(list);
    }
  }, [pageType, isInternal]);

  const dangerVariant = useMemo(() => {
    if (error) {
      if (error.variant === "danger") {
        return true;
      }
    }
    return false;
  }, [error]);

  useEffect(() => {
    if (!mixpanelTracked) {
      if (initialNav && pageType) {
        Mixpanel.register({ page_group: pageType, active_page: initialNav });
        Mixpanel.track_pageview({ page: initialNav });
        setMixpanelTracked(true);
      } else if (pageType) {
        Mixpanel.register({ page_group: pageType, active_page: pageType });
        Mixpanel.track_pageview({ page: pageType });
        setMixpanelTracked(true);
      } else if (initialNav) {
        Mixpanel.register({ active_page: initialNav, page_group: initialNav });
        Mixpanel.track_pageview({ page: initialNav });
        setMixpanelTracked(true);
      } else {
        // we dont know what page it is
        Mixpanel.register({ active_page: null, page_group: null });
      }
    }
  }, [initialNav, pageType, mixpanelTracked]);

  const errorModal = error ? (
    <Modal show={true} onHide={error.onOkay} centered={Boolean(error.centered)}>
      <Alert variant={error.variant as AlertProps["variant"]} className="BPMModalAlert">
        <Alert.Heading>{error.title}</Alert.Heading>
        {formatMessage(errorParams.message)}
        <hr />
        <div className="modalFooter">
          <Button
            variant={`outline-${error.variant}` as AlertProps["variant"]}
            onClick={error.onOkay}
          >
            {error.okayText}
          </Button>
          {dangerVariant ? (
            <Button
              variant={`outline-${error.variant}` as AlertProps["variant"]}
              onClick={() => setShowBugTicketModal(true)}
            >
              {error.cancelText}
            </Button>
          ) : null}
        </div>
      </Alert>
    </Modal>
  ) : (
    <div />
  );
  const areYouSureModal = areYouSure ? (
    <Modal show={true} onHide={areYouSure.onCancel} centered={Boolean(areYouSure.centered)}>
      <Alert variant={areYouSure.variant as AlertProps["variant"]} className="BPMModalAlert">
        <Alert.Heading>{areYouSure.title}</Alert.Heading>
        {formatMessage(areYouSure.message)}
        <hr />
        <div className="modalFooter">
          <Button
            variant={`outline-${areYouSure.variant}` as AlertProps["variant"]}
            onClick={areYouSure.onCancel}
          >
            {areYouSure.cancelText}
          </Button>
          <Button variant={areYouSure.variant} onClick={areYouSure.onOkay}>
            {areYouSure.okayText}
          </Button>
        </div>
      </Alert>
    </Modal>
  ) : (
    <div />
  );
  if (onlyModals) {
    return (
      <div>
        {errorModal}
        {areYouSureModal}
      </div>
    );
  }
  return (
    <div className="bpmPage" style={{ minHeight, minWidth }}>
      <ErrorBoundary>
        <NewVersionAlertBar />
        <div className={cn("header", { app2Redesign })}>
          <div className="titleContainer">
            {app2Redesign && (
              <>
                <Img
                  className="blissPointLogo"
                  alt={"Bliss Point"}
                  src="https://cdn.blisspointmedia.com/agencies/bpm/Tinuiti_Blisspoint_Logo_White.png"
                  unloader={<span>Bliss Point</span>}
                />
                <div className="divider" />
              </>
            )}
            {title && <div className={cn("title", { app2Redesign })}>{title}</div>}
            {navs && (
              <Nav className="navs" variant="pills" activeKey={selectedNav} onSelect={ourOnNav}>
                {navs.map(({ label, key }) => (
                  <Nav.Item key={key}>
                    <Nav.Link {...(app2Redesign ? { bsPrefix: "pageNavItem" } : {})} eventKey={key}>
                      {navRenderer(label)}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            )}
          </div>
          {showBugTicketModal && (
            <BugTicketModal
              setShowBugTicketModal={setShowBugTicketModal}
              bugErrorParams={errorParams}
            />
          )}
          {showHelpVideoTutorial && (
            <VideoTutorial showHelpButton={showHelpVideoTutorial} videoList={videoList} />
          )}
          <div className={cn("actions", { app2Redesign })}>{actions}</div>
        </div>
        {subHeader && <div className="pageSubHeader">{subHeader}</div>}
        <div className="body">{children}</div>
        {errorModal}
        {areYouSureModal}
      </ErrorBoundary>
    </div>
  );
};

export default Page;
